import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Image,
    Input,
    Text,
    useDisclosure,
    useColorModeValue,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { SponsorDto } from '../../../models/sponsor/dto/sponsor.dto';
import { CreateSponsorInputDto } from '../../../models/sponsor/dto/create-sponsor.dto';
import { CreateSponsorWithLogoInput, useCreateSponsor } from '../hooks/useCreateSponsor';
import { UpdateSponsorWithLogoInput, useUpdateSponsor } from '../hooks/useUpdateSponsor';

const defaultValues: CreateSponsorInputDto = {
    name: '',
    websiteUrl: '',
    isActive: true,
};

interface CreateSponsorFormProps {
    sponsor?: SponsorDto;
    isOpen?: boolean;
    onClose?: () => void;
}

export default function CreateSponsorForm({ sponsor, isOpen: externalIsOpen, onClose: externalOnClose }: CreateSponsorFormProps = {}) {
    const { mutateAsync: createSponsor, isLoading: isCreating } = useCreateSponsor();
    const { mutateAsync: updateSponsor, isLoading: isUpdating } = useUpdateSponsor();
    const [formData, setFormData] = useState<CreateSponsorInputDto>(defaultValues);
    const [errors, setErrors] = useState<Partial<Record<keyof CreateSponsorInputDto, string>>>({});
    const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toast = useToast();
    const dropzoneBorderColor = useColorModeValue('gray.300', 'whiteAlpha.300');
    const dropzoneHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
    const dropzoneTextColor = useColorModeValue('gray.600', 'gray.400');
    const previewBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: externalIsOpen });

    useEffect(() => {
        if (externalIsOpen !== undefined) {
            if (externalIsOpen) {
                onOpen();
            } else {
                onClose();
            }
        }
    }, [externalIsOpen, onOpen, onClose]);

    useEffect(() => {
        if (sponsor) {
            setFormData({
                name: sponsor.name,
                websiteUrl: sponsor.websiteUrl || '',
                isActive: sponsor.isActive,
            });
            // Set logo preview if sponsor has a logoUrl
            if (sponsor.logoUrl) {
                setLogoPreview(sponsor.logoUrl);
            }
        } else {
            setFormData(defaultValues);
            setSelectedLogo(null);
            setLogoPreview(null);
        }
    }, [sponsor]);

    const handleInputChange = (field: keyof CreateSponsorInputDto, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedLogo(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveLogo = () => {
        setSelectedLogo(null);
        setLogoPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        // Also clear logoUrl from formData if it exists
        setFormData(prev => ({ ...prev, logoUrl: undefined }));
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) {
            setSelectedLogo(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const validate = (): boolean => {
        const newErrors: Partial<Record<keyof CreateSponsorInputDto, string>> = {};
        if (!formData.name.trim()) {
            newErrors.name = translate('sponsor.name_required') || 'Name is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        try {
            if (sponsor) {
                // Use logo upload format if logo file is selected, otherwise use regular format
                if (selectedLogo) {
                    const updateData: UpdateSponsorWithLogoInput = {
                        sponsorData: {
                            name: formData.name,
                            websiteUrl: formData.websiteUrl,
                            isActive: formData.isActive,
                        },
                        logoFile: selectedLogo,
                    };
                    await updateSponsor({ id: sponsor.id, input: updateData });
                } else {
                    await updateSponsor({ id: sponsor.id, input: formData });
                }
                toast({
                    title: translate('sponsor.update_success') || 'Sponsor updated successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                // Use logo upload format if logo file is selected, otherwise use regular format
                if (selectedLogo) {
                    const createData: CreateSponsorWithLogoInput = {
                        sponsorData: {
                            name: formData.name,
                            websiteUrl: formData.websiteUrl,
                            isActive: formData.isActive,
                        },
                        logoFile: selectedLogo,
                    };
                    await createSponsor(createData);
                } else {
                    await createSponsor(formData);
                }
                toast({
                    title: translate('sponsor.create_success') || 'Sponsor created successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }
            setFormData(defaultValues);
            setSelectedLogo(null);
            setLogoPreview(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            onClose();
            externalOnClose?.();
        } catch (error: any) {
            toast({
                title: translate('sponsor.error') || 'Error',
                description: error.message || 'An error occurred',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleClose = () => {
        setFormData(defaultValues);
        setErrors({});
        setSelectedLogo(null);
        setLogoPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
        externalOnClose?.();
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={handleClose}
            title={sponsor ? translate('sponsor.edit') || 'Edit Sponsor' : translate('sponsor.create') || 'Create Sponsor'}
            onSubmit={handleSubmit}
            submitLabel={sponsor ? translate('common.save') || 'Save' : translate('common.create') || 'Create'}
            cancelLabel={translate('common.cancel') || 'Cancel'}
            isSubmitting={isCreating || isUpdating}
            size="lg"
        >
            <FormStack>
                <FormField
                    type="text"
                    label={translate('sponsor.name') || 'Name'}
                    value={formData.name}
                    onChange={(value) => handleInputChange('name', value)}
                    placeholder={translate('sponsor.name_placeholder') || 'Enter sponsor name'}
                    error={errors.name}
                />

                <FormField
                    type="url"
                    label={translate('sponsor.website') || 'Website URL (optional)'}
                    value={formData.websiteUrl}
                    onChange={(value) => handleInputChange('websiteUrl', value)}
                    placeholder="https://example.com"
                />

                <FormControl>
                    <FormLabel>{translate('sponsor.logo') || 'Logo'}</FormLabel>
                    <VStack align="start" spacing={3}>
                        <Input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            display="none"
                        />
                        <Box
                            width="100%"
                            p={4}
                            border="2px dashed"
                            borderColor={dropzoneBorderColor}
                            borderRadius="md"
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            _hover={{ borderColor: 'blue.400', bg: dropzoneHoverBg }}
                            cursor="pointer"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Text textAlign="center" color={dropzoneTextColor}>
                                {translate('sponsor.drag_drop_logo') || 'Drag & drop a logo here, or click to select'}
                            </Text>
                        </Box>
                        <HStack>
                            <Button
                                onClick={() => fileInputRef.current?.click()}
                                variant="outline"
                                size="sm"
                            >
                                {translate('sponsor.select_logo') || 'Select Logo'}
                            </Button>
                            {(selectedLogo || (sponsor && sponsor.logoUrl)) && (
                                <Button
                                    onClick={handleRemoveLogo}
                                    variant="ghost"
                                    size="sm"
                                    colorScheme="red"
                                >
                                    {translate('common.remove') || 'Remove'}
                                </Button>
                            )}
                        </HStack>
                        {logoPreview && (
                            <Box>
                                <Text fontSize="sm" mb={2}>
                                    {translate('sponsor.preview') || 'Preview:'}
                                </Text>
                                <Image
                                    src={logoPreview}
                                    alt="Logo preview"
                                    maxW="200px"
                                    maxH="200px"
                                    objectFit="contain"
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor={previewBorderColor}
                                />
                            </Box>
                        )}
                        {selectedLogo && (
                            <Text fontSize="sm" color={dropzoneTextColor}>
                                {translate('sponsor.selected_file') || 'Selected'}: {selectedLogo.name}
                            </Text>
                        )}
                    </VStack>
                </FormControl>

                <FormField
                    type="switch"
                    label={translate('sponsor.active') || 'Active'}
                    isChecked={formData.isActive}
                    onChange={(checked) => handleInputChange('isActive', checked)}
                />
            </FormStack>
        </FormModal>
    );
}
